import { Link, useParams } from "react-router-dom";
import {
  episodes,
  getCastForEpisode,
  getEpisodeById,
  getRelatedEpisodes,
} from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/feature/Breadcrumb";
import DemoPlayer from "./components/DemoPlayer";
import EpisodeActions from "./components/EpisodeActions";
import CastStrip from "./components/CastStrip";
import RelatedEpisodes from "./components/RelatedEpisodes";
import EpisodeDirectory from "./components/EpisodeDirectory";

export default function DetailPage() {
  const { episodeId } = useParams<{ episodeId: string }>();
  const episode = episodeId ? getEpisodeById(episodeId) : undefined;
  const cast = episode ? getCastForEpisode(episode) : [];
  const related = episode ? getRelatedEpisodes(episode, 3) : [];
  const { isFavorite, toggle } = useFavorites();

  const sorted = [...episodes].sort((a, b) => a.number - b.number);
  const currentIndex = episode ? sorted.findIndex((item) => item.id === episode.id) : -1;
  const prev = currentIndex > 0 ? sorted[currentIndex - 1] : undefined;
  const next =
    currentIndex >= 0 && currentIndex < sorted.length - 1 ? sorted[currentIndex + 1] : undefined;

  useSeo(
    episode ? `${episode.title}剧情介绍 - 出轨同学会` : "剧集详情 - 出轨同学会",
    episode
      ? `出轨同学会第${episode.number}集《${episode.title}》剧情介绍：${episode.summary}`
      : "出轨同学会剧集详情与剧情介绍。",
    episode
      ? `出轨同学会第${episode.number}集,${episode.title},${cast.map((item) => item.name).join(",")},出轨同学会剧情`
      : undefined,
  );

  if (!episode) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-20 md:px-8 lg:px-12">
        <div className="flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-2xl leading-none"></i>
          </span>
          <h1 className="font-heading text-xl font-bold text-foreground-900">剧集不存在</h1>
          <p className="font-label text-sm text-foreground-500">
            没有找到对应的剧集，可能链接已失效，请返回剧集更新列表重新选择。
          </p>
          <Link
            to="/category/updates"
            className="rounded-md bg-primary-700 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-800"
          >
            浏览全部剧集
          </Link>
        </div>
      </div>
    );
  }

  const metaRows = [
    { label: "上映日期", value: episode.airDate },
    { label: "单集时长", value: episode.duration },
    { label: "导演", value: episode.director },
    { label: "编剧", value: episode.screenwriter },
  ];

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8 md:py-8 lg:px-12">
      <Breadcrumb
        items={[
          { label: "剧集更新", to: "/category/updates" },
          { label: `第${episode.number}集 ${episode.title}` },
        ]}
      />

      <header className="mt-5 grid grid-cols-1 gap-6 rounded-lg border border-background-200 bg-background-50 p-5 md:grid-cols-[300px_1fr] md:p-6">
        <div className="aspect-video w-full overflow-hidden rounded-md bg-background-200 md:aspect-[4/3]">
          <img
            src={episode.cover}
            alt={`出轨同学会第${episode.number}集《${episode.title}》剧照海报`}
            title={`出轨同学会 第${episode.number}集 ${episode.title}`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="flex flex-col">
          <span className="w-fit rounded-full bg-primary-100 px-3 py-1 font-label text-xs font-medium text-primary-800">
            共 {episodes.length} 集 · 第 {episode.number} 集
          </span>
          <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-900 md:text-3xl">
            {episode.title}
          </h1>
          <p className="mt-3 font-label text-sm leading-relaxed text-foreground-600">
            {episode.summary}
          </p>

          <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-background-200 pt-4">
            {metaRows.map((row) => (
              <div key={row.label} className="flex flex-col">
                <dt className="font-label text-[11px] text-foreground-400">{row.label}</dt>
                <dd className="mt-0.5 truncate font-label text-sm text-foreground-800">{row.value}</dd>
              </div>
            ))}
            <div className="col-span-2 flex flex-col">
              <dt className="font-label text-[11px] text-foreground-400">主演</dt>
              <dd className="mt-0.5 font-label text-sm text-foreground-800">
                {cast.map((item) => `${item.name} 饰 ${item.role}`).join(" · ")}
              </dd>
            </div>
          </dl>

          <div className="mt-5 flex flex-wrap gap-2">
            {episode.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>
      </header>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px] lg:gap-10">
        <div className="flex min-w-0 flex-col gap-10">
          <section>
            <DemoPlayer episode={episode} />
            <div className="mt-4">
              <EpisodeActions
                episode={episode}
                isFavorite={isFavorite(episode.id)}
                onToggleFavorite={() => toggle(episode.id)}
                prev={prev}
                next={next}
              />
            </div>
          </section>

          <section>
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-book-open-line text-lg leading-none"></i>
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-900">剧情介绍</h2>
            </div>
            <div className="mt-5 flex flex-col gap-5 rounded-lg border border-background-200 bg-background-50 p-6">
              {episode.synopsis.map((paragraph, index) => (
                <p
                  key={index}
                  className="font-label text-[15px] leading-[1.9] text-foreground-700"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-user-star-line text-lg leading-none"></i>
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-900">主演信息</h2>
            </div>
            <div className="mt-5">
              <CastStrip cast={cast} />
            </div>
          </section>

          <section>
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className="ri-links-line text-lg leading-none"></i>
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-900">相关推荐</h2>
            </div>
            <div className="mt-5">
              <RelatedEpisodes episodes={related} />
            </div>
          </section>
        </div>

        <aside className="flex flex-col gap-6">
          <EpisodeDirectory episodes={episodes} currentId={episode.id} />

          <section className="rounded-lg border border-background-200 bg-secondary-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-900">
              <span className="flex h-4 w-4 items-center justify-center text-secondary-700">
                <i className="ri-bookmark-line text-base leading-none"></i>
              </span>
              我的收藏
            </h2>
            <p className="mt-3 font-label text-sm leading-relaxed text-foreground-600">
              {isFavorite(episode.id)
                ? "本集已加入你的收藏，收藏记录仅保存在本机浏览器中。"
                : "点击“收藏本集”，即可把这一集保存到本机浏览器，方便下次快速回访。"}
            </p>
            <button
              type="button"
              onClick={() => toggle(episode.id)}
              className={`mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 font-label text-sm font-medium transition-colors ${
                isFavorite(episode.id)
                  ? "border border-accent-400 bg-background-50 text-accent-700 hover:bg-accent-50"
                  : "bg-accent-500 text-primary-950 hover:bg-accent-400"
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={isFavorite(episode.id) ? "ri-heart-fill text-base leading-none" : "ri-heart-line text-base leading-none"}></i>
              </span>
              {isFavorite(episode.id) ? "取消收藏" : "收藏本集"}
            </button>
          </section>
        </aside>
      </div>
    </div>
  );
}